import { useTheme, Box, TextV2 } from '@withjoy/joykit';
import React, { useEffect, useState } from 'react';
import { StyledProgressRing, StyledIconWrapper, StyledProgressRingWrapper, styles, StyledSvg, StyledCheckFilled } from './styles';
import { ReactComponent as Checkmark } from '../../assets/thick-check.svg';
import { DEFAULT_GRADIENT_COLORS, DEFAULT_PROGRESS_RING_DIMENSIONS, ProgressRingProps } from './types';

export const ProgressRing = ({
  progressPercent,
  isOpen,
  currentGiftCount,
  children,
  dimensions = DEFAULT_PROGRESS_RING_DIMENSIONS,
  showCheck = true,
  useGradient = false,
  progressColor = '#4E06F2',
  gradientColors = DEFAULT_GRADIENT_COLORS,
  countTextColor,
  typographyVariant,
}: ProgressRingProps) => {
  const { colors } = useTheme();
  const [progress, setProgress] = useState<number | undefined>(undefined);

  const { width, height, cx, cy, r, strokeWidth } = dimensions;
  const circumference = r * 2 * Math.PI;
  const textColor = countTextColor || colors.mono14;

  useEffect(() => {
    const progress = (() => {
      if (progressPercent / 100 >= 1) {
        return 1;
      } else {
        return circumference - (progressPercent / 100) * circumference;
      }
    })();

    if (!isOpen) {
      setProgress(undefined);
      return;
    }
    const timer = setTimeout(() => {
      setProgress(progress);
    }, 500);
    return () => clearTimeout(timer);
  }, [circumference, progressPercent, isOpen]);

  const renderContent = () => {
    if (children) return children;
    if (progress === 100) return <Checkmark />;
    if (typeof currentGiftCount === 'number') {
      return (
        <TextV2
          typographyVariant={typographyVariant || (currentGiftCount > 999 ? 'hed1' : 'hed3')}
          color={textColor}
        >
          {currentGiftCount}
        </TextV2>
      );
    }
    return null;
  };

  const gradientId = React.useMemo(() => `progressGradient-${Math.random().toString(36).substr(2, 9)}`, []);

  return (
    <StyledProgressRingWrapper
      __css={styles.progressRingWrapper}
      dimensions={dimensions}
    >
      <Box position="relative">
        <StyledSvg
          viewBox={`0 0 ${width} ${height}`}
          dimensions={dimensions}
        >
          {useGradient && (
            <defs>
              <linearGradient
                id={gradientId}
                x1="5.5"
                y1="20.0384"
                x2="36.4231"
                y2="20.0384"
                gradientUnits="userSpaceOnUse"
              >
                <stop
                  offset="0"
                  style={{
                    stopColor: gradientColors.startColor.p3 || gradientColors.startColor.hex,
                    stopOpacity: 1,
                  }}
                />
                <stop
                  offset="1"
                  style={{
                    stopColor: gradientColors.endColor.p3 || gradientColors.endColor.hex,
                    stopOpacity: 1,
                  }}
                />
              </linearGradient>
            </defs>
          )}

          <StyledProgressRing
            dashoffset={circumference}
            strokeWidth={strokeWidth}
            stroke={colors.mono4}
            strokeLinecap="round"
            cx={cx}
            cy={cy}
            r={r}
          />

          <StyledProgressRing
            strokeWidth={strokeWidth}
            stroke={useGradient ? `url(#${gradientId})` : progressColor} // Use unique ID here
            strokeDasharray={`${circumference} ${circumference}`}
            dashoffset={circumference}
            progress={progress}
            strokeLinecap="round"
            cx={cx}
            cy={cy}
            r={r}
          />
        </StyledSvg>
        {progressPercent >= 100 && showCheck && (
          <StyledCheckFilled
            color={useGradient ? gradientColors.startColor.hex : progressColor}
            size="sm"
            __css={styles.checkFilled}
          />
        )}
      </Box>
      <StyledIconWrapper __css={styles.iconWrapper}>{renderContent()}</StyledIconWrapper>
    </StyledProgressRingWrapper>
  );
};
